import {CHANNEL_INFO_BY_CHANNEL, CHANNEL_TYPE_PAID_MEDIA} from '../channels/channels';
import {
    METHOD_INCLUDE_ALL_CAMPAIGNS, METHOD_INCLUDE_CAMPAIGN_ON_CONDITION, METHOD_SELECT_CAMPAIGNS_FROM_LIST,
} from '../business_units/accounts/selectionMethods';

/**
 * Qué campañas «entran hoy» a una cuenta de la línea, y qué tan completa está su configuración.
 *
 * El preview es lo que hace que la tarjeta se entienda (Pablo, 29/07: «se incluyen según su nombre:
 * A» es lo que deja claro qué mira la línea), así que tiene que calcular EXACTAMENTE lo mismo que el
 * backend (`BusinessUnitCampaignsConfiguration.assign_on_condition_campaigns_of_ad_account`):
 *
 * - no distingue mayúsculas, sí acentos;
 * - varios términos van separados por `|||`; los de «incluir» se combinan con Y y los de «excluir»
 *   con O, cada uno recortado;
 * - una condición vacía no filtra.
 *
 * Una diferencia a propósito: el backend asigna sobre TODAS las campañas de la cuenta y acá se cuentan
 * sólo las activas, porque el preview dice «entran hoy · N (sólo activas)».
 */

export const WIZARD_METHODS = [METHOD_INCLUDE_CAMPAIGN_ON_CONDITION, METHOD_SELECT_CAMPAIGNS_FROM_LIST, METHOD_INCLUDE_ALL_CAMPAIGNS];

const SEPARATOR = '|||';

function terms(text) {
    if (text === null || text === undefined || String(text).trim() === '') return null;
    return String(text).toLowerCase().split(SEPARATOR).map(term => term.trim());
}

export function matchesCondition(name, includeText, excludeText) {
    const lower = String(name || '').toLowerCase();
    const include = terms(includeText);
    const exclude = terms(excludeText);
    const included = !include || include.every(term => lower.includes(term));
    const excluded = !!exclude && exclude.some(term => lower.includes(term));
    return included && !excluded;
}

/** ¿Esta cuenta elige campañas? Sólo la paid media: GA4 y las tiendas entran enteras. */
export function selectsCampaigns(channel) {
    return CHANNEL_INFO_BY_CHANNEL[channel]?.channelType === CHANNEL_TYPE_PAID_MEDIA;
}

/**
 * Las campañas activas que la configuración incluye hoy. `null` si la cuenta todavía no sincronizó
 * su catálogo (`all_active_account_campaigns` viene en `null`): ahí no hay número honesto que dar.
 */
export function campaignsInToday(config) {
    const active = config.all_active_account_campaigns;
    if (active === null || active === undefined) return null;
    if (config.selection_method === METHOD_INCLUDE_ALL_CAMPAIGNS) return active;
    if (config.selection_method === METHOD_INCLUDE_CAMPAIGN_ON_CONDITION) {
        return active.filter(campaign => matchesCondition(campaign.name, config.include_containing, config.exclude_containing));
    }
    if (config.selection_method === METHOD_SELECT_CAMPAIGNS_FROM_LIST) {
        const selected = new Set((config.selected_campaigns || []).map(String));
        return active.filter(campaign => selected.has(String(campaign.id)));
    }
    return [];
}

const CAMPAIGN_METHODS = [METHOD_INCLUDE_ALL_CAMPAIGNS, METHOD_INCLUDE_CAMPAIGN_ON_CONDITION, METHOD_SELECT_CAMPAIGNS_FROM_LIST];

/**
 * Lo que la configuración deja AFUERA hoy y cuánto invirtió en el período actual (caso Kendal, 15/10: la
 * regla «no contar las que contienen “cyber”» dejó afuera el Cyber de octubre y la vista previa sólo decía
 * cuántas entraban). `{count, spend, currency}`:
 *
 * - `count`: las activas que NO entran hoy, con la misma regla que «entran hoy» (`campaignsInToday`), así
 *   las dos cifras siempre suman el total de la cuenta;
 * - `spend`: la suma de su `spend_current_period` (contrato §2.2, en la moneda de la cuenta), del lado del
 *   cliente porque cambia mientras el usuario escribe la condición;
 * - `currency`: la de `spend_period`.
 *
 * `null` —y la vista previa queda como siempre— si el backend no manda `spend_period` (backend viejo), si
 * la cuenta no elige campañas, si todavía no sincronizó su catálogo o si elige por grupos de anuncios
 * (ahí una campaña puede entrar a medias y la cuenta no sería honesta).
 */
export function campaignsLeftOutToday(config) {
    if (!config || !config.spend_period || !selectsCampaigns(config.channel)) return null;
    if (!CAMPAIGN_METHODS.includes(config.selection_method)) return null;
    const active = config.all_active_account_campaigns;
    const inToday = campaignsInToday(config);
    if (!Array.isArray(active) || inToday === null) return null;

    const included = new Set(inToday.map(campaign => String(campaign.id)));
    // El clásico manda también las que no corren, marcadas con `is_active: false`: «hoy» son las activas.
    const out = active.filter(campaign => campaign.is_active !== false && !included.has(String(campaign.id)));
    const spend = out.reduce((total, campaign) => total + (Number(campaign.spend_current_period) || 0), 0);
    return {count: out.length, spend, currency: config.spend_period.currency || null};
}

/**
 * Qué lista «Elegirlas manualmente de una lista»: `{campaigns, hiddenCount, canToggle}`.
 *
 * Por defecto, sin las que no están corriendo (punto 12 del recorrido de fede, 01/10): la lista
 * viene en vivo de la plataforma con las pausadas, finalizadas y borradas, y en una cuenta con
 * historia eran la mayoría.
 *
 * - **Se esconde sólo lo que se SABE inactivo** (`inactiveIds`, de `campaigns/inactive`: lo mismo
 *   que usa el «Sólo activos» del clásico). Lo que no se conoce queda a la vista: filtrar por «no
 *   está entre las activas» escondía la campaña que alguien acaba de lanzar, que todavía no llegó a
 *   la base, justo la que quiere medir (review C-rec-fe-linea-02).
 * - `pinned` se ve siempre, inactiva o no: las que venían elegidas y las que el usuario tildó en esta
 *   apertura. Una línea que ya mira una pausada tiene que poder verla para sacarla, y destildar una
 *   no puede hacerla desaparecer en el mismo clic (C-rec-fe-linea-03).
 * - Si no hay ninguna que no sea inactiva, o no se sabe cuáles lo son, filtrar dejaría la lista
 *   vacía o mentiría: se muestran todas y no se ofrece el filtro.
 * - `hiddenCount` cuenta las que el filtro está escondiendo, no las inactivas a la vista.
 */
export function campaignsToList({campaigns, inactiveIds, showInactive, pinned}) {
    const all = campaigns || [];
    const known = inactiveIds || new Set();
    const isInactive = campaign => known.has(String(campaign.id));
    const inactiveCount = all.filter(isInactive).length;
    const canToggle = inactiveCount > 0 && inactiveCount < all.length;
    const kept = new Set([...(pinned || [])].map(String));
    const hidden = campaign => isInactive(campaign) && !kept.has(String(campaign.id));
    const filtering = !showInactive && canToggle;
    return {
        campaigns: filtering ? all.filter(campaign => !hidden(campaign)) : all,
        hiddenCount: all.filter(hidden).length,
        canToggle,
    };
}

/**
 * Los eventos de una cuenta con sus conteos 1D / 7D / 30D, por `action_id`.
 *
 * GA4 no trae los conteos con `/actions` (los otros canales sí): salen de `actions/counts`, como en
 * `ConfigureEventsStep` y `GoalConfiguration`. Con `counts` en cada evento, el selector del clásico
 * dibuja solo los badges, el filtro «Con datos» y la frase de cobertura (punto 13).
 */
export function mergeActionCounts(actions, countsByActionId) {
    if (!actions || !countsByActionId) return actions;
    return actions.map(action => {
        const counts = countsByActionId[action.action_id];
        return counts ? {...action, counts} : action;
    });
}

/**
 * Sin conversión elegida la tarjeta va EN ROJO (feedback del 29/07: es uno de los dos campos que
 * «siempre le falta poner a la gente»).
 *
 * Una selección vacía no siempre es «nada»: en los canales que tienen conversiones primarias
 * (Google, TikTok, LinkedIn…) vacío quiere decir «todas las primarias», que es una elección válida —
 * el backend lo deriva así al guardar. Meta no tiene esa opción: vacío es no medir nada.
 */
export function conversionState(config) {
    const info = CHANNEL_INFO_BY_CHANNEL[config.channel] || {};
    if (!info.hasActions) return 'NOT_APPLICABLE';
    if ((config.selected_actions || []).length > 0) return 'SELECTED';
    if (info.hasIncludeAllConversionsSetting) return 'ALL_PRIMARY';
    return 'MISSING';
}

/** Una condición por nombre sin ningún texto la rechaza el backend (`ERROR_CONDITION_…_INVALID`). */
export function selectionIsValid(config) {
    if (!selectsCampaigns(config.channel)) return true;
    if (config.selection_method === METHOD_INCLUDE_CAMPAIGN_ON_CONDITION) {
        return !!(terms(config.include_containing) || terms(config.exclude_containing));
    }
    if (config.selection_method === METHOD_SELECT_CAMPAIGNS_FROM_LIST) {
        return (config.selected_campaigns || []).length > 0;
    }
    return true;
}

/**
 * El cuerpo del PUT de `campaigns-configurations/edit`.
 *
 * El backend lee con `[]` una docena de claves del BUCC (los días, el método, las acciones, la
 * cuenta, GA4…), así que se manda el objeto ENTERO tal como vino del GET, con las ediciones encima —
 * el mismo criterio que el clásico (`BusinessUnitAccounts.campaignConfigurationsWithChangesToPost`).
 * Se sacan sólo las listas de catálogo, que no se guardan y pesan.
 */
export function editPayload(configurations) {
    const payload = {};
    Object.keys(configurations).forEach(configId => {
        const config = {...configurations[configId]};
        delete config.all_active_account_adgroups;
        delete config.all_active_account_campaigns;
        delete config.all_not_active_account_adgroups;
        delete config.all_not_active_account_campaigns;
        delete config.all_actions;
        payload[configId] = config;
    });
    return payload;
}
